<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import GaiaStatusIndicator, { type GaiaIndicatorStatus } from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaTooltip from '../base/GaiaTooltip.vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** What was run — the document or contract name. */
  input: {
    type: String,
    required: true,
  },
  /** Formatted creation timestamp. */
  created: {
    type: String,
    default: undefined,
  },
  /** Who started the run — their name or email address, named on the avatar's hover. */
  startedBy: {
    type: String,
    default: undefined,
  },
  /** That person's initials, e.g. "JM" — derived by the caller, per `initialsOf`. */
  startedByInitials: {
    type: String,
    default: '',
  },
  /** Run status — adapt PlaybookRunStatus via statusMaps.playbookRunStatusMap. */
  status: {
    type: String as PropType<GaiaIndicatorStatus>,
    default: 'pending',
  },
  /** Human status label — the dot's hover names the state. */
  statusLabel: {
    type: String,
    default: undefined,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ select: [] }>();

const divider = gaiaNeutral.borderSoft;
const ink = gaiaNeutral.ink;
const surface = gaiaNeutral.surface;
const hover = gaiaNeutral.surfaceHover;
// The row is a button whose title bolds on the active state, so its type is a
// selector-bound rule rather than a GaiaText prop.
const titleSize = gaiaFontSize.dense;
const medium = String(gaiaFontWeight.medium);
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- One run in the playbook's run rail (Playbooks mock, "Per run" view).
       Selected rows get the ink marker and a white surface. -->
  <button
    type="button"
    class="gaia-run-list-item"
    :class="{ selected: props.selected }"
    @click="emit('select')"
  >
    <gaia-tooltip :disabled="!props.statusLabel">
      <template #trigger>
        <n-flex inline :size="0" :wrap="false" :style="{ flex: 'none', marginTop: '3px' }">
          <gaia-status-indicator :status="props.status" />
        </n-flex>
      </template>
      {{ props.statusLabel }}
    </gaia-tooltip>
    <n-flex vertical :size="3" :style="{ minWidth: 0 }">
      <span class="input">{{ props.input }}</span>
      <gaia-text
        v-if="props.created"
        size="meta"
        tone="subtle"
        :style="{ fontVariantNumeric: 'tabular-nums' }"
      >
        {{ props.created }}
      </gaia-text>
      <!-- The disc rather than the address: the rail is too narrow to spell one
           out, and its hover names the person anyway. -->
      <gaia-tooltip v-if="props.startedBy">
        <template #trigger>
          <gaia-avatar :size="24" :style="{ flex: 'none', alignSelf: 'flex-start' }">
            {{ props.startedByInitials }}
          </gaia-avatar>
        </template>
        {{ props.startedBy }}
      </gaia-tooltip>
    </n-flex>
  </button>
</template>

<style scoped>
.gaia-run-list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 11px 16px;
  border: none;
  border-bottom: 1px solid v-bind(divider);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.gaia-run-list-item:hover {
  background: v-bind(hover);
}

/* The selected marker is an inset bar, so the row keeps its own hairline. */
.gaia-run-list-item.selected {
  background: v-bind(surface);
  box-shadow: inset 2px 0 0 v-bind(ink);
}

.input {
  font-size: v-bind(titleSize);
  font-weight: v-bind(medium);
  color: v-bind(ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected .input {
  font-weight: v-bind(bold);
}
</style>
